import React, { useEffect, useState, useCallback } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	Divider,
	ListItem,
	ListItemIcon,
	ListItemText,
	Paper,
	Typography,
	IconButton,
	useTheme,
	alpha,
	Stack,
	Tooltip,
	Zoom
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
	VolumeUp,
	Check,
	NotificationsActive,
	NotificationsOff,
	PlayArrow,
	Pause
} from "@mui/icons-material";
import { i18n } from "../../translate/i18n";

// Notification sounds
import sound from "../../assets/sound.mp3";
import alertBetacut from "../../assets/alert-betacut.mp3";
import gtaSound from "../../assets/gta-sound.mp3";
import bell from "../../assets/bell.mp3";
import livechat from "../../assets/livechat.mp3";
import phoneRing from "../../assets/phone-ring.mp3";

// Styled components
const StyledDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		borderRadius: 12,
		boxShadow: theme.shadows[10],
		overflow: "hidden",
		maxWidth: "550px",
		width: "100%"
	}
}));

const CategoryPaper = styled(Paper)(({ theme }) => ({
	borderRadius: 8,
	marginBottom: theme.spacing(2),
	backgroundColor: theme.palette.mode === "dark"
		? alpha(theme.palette.background.paper, 0.6)
		: theme.palette.background.paper,
	boxShadow: theme.shadows[2],
	overflow: "hidden",
	transition: "transform 0.2s ease, box-shadow 0.2s ease",
	"&:hover": {
		boxShadow: theme.shadows[4],
		transform: "translateY(-2px)"
	}
}));

const CategoryHeader = styled(Box)(({ theme }) => ({
	padding: theme.spacing(2),
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`
}));

const AudioListItem = styled(ListItem)(({ theme, selected }) => ({
	padding: theme.spacing(1.5, 2),
	transition: "background-color 0.2s ease",
	borderRadius: 4,
	margin: theme.spacing(0.5, 1),
	backgroundColor: selected
		? alpha(theme.palette.primary.main, 0.1)
		: "transparent",
	"&:hover": {
		backgroundColor: selected
			? alpha(theme.palette.primary.main, 0.15)
			: alpha(theme.palette.action.hover, 0.1)
	}
}));

const PlayButton = styled(IconButton)(({ theme, isplaying }) => ({
	color: isplaying === "true"
		? theme.palette.primary.main
		: theme.palette.text.secondary,
	backgroundColor: isplaying === "true"
		? alpha(theme.palette.primary.main, 0.1)
		: "transparent",
	"&:hover": {
		backgroundColor: isplaying === "true"
			? alpha(theme.palette.primary.main, 0.2)
			: alpha(theme.palette.action.hover, 0.1)
	}
}));

// Audio file data structure
const audioFiles = [
	{ id: "sound", name: "Padrão", url: sound, icon: VolumeUp },
	{ id: "alertBetacut", name: "Alerta", url: alertBetacut, icon: NotificationsActive },
	{ id: "gtaSound", name: "GTA", url: gtaSound, icon: VolumeUp },
	{ id: "bell", name: "Sino", url: bell, icon: NotificationsActive },
	{ id: "livechat", name: "Chat", url: livechat, icon: VolumeUp },
	{ id: "phoneRing", name: "Telefone", url: phoneRing, icon: VolumeUp }
];

/**
 * AudioSelectCategory component for selecting notification sounds
 */
const AudioSelectCategory = ({
	title,
	icon,
	selectedAudio,
	onAudioChange,
	playingAudio,
	onPlayAudio
}) => {
	const theme = useTheme();

	return (
		<CategoryPaper elevation={0}>
			<CategoryHeader>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1.5,
						color: theme.palette.primary.main
					}}
				>
					{icon}
					<Typography variant="h6" fontWeight="600">
						{title}
					</Typography>
				</Box>
			</CategoryHeader>

			<List sx={{ py: 1 }}>
				{audioFiles.map((audio) => {
					const isSelected = audio.id === selectedAudio;
					const isPlaying = playingAudio === audio.id;
					const AudioIcon = audio.icon;

					return (
						<AudioListItem
							key={audio.id}
							button
							selected={isSelected}
							onClick={() => onAudioChange(audio.id)}
							disableRipple
						>
							<Box sx={{
								display: "flex",
								alignItems: "center",
								flex: 1,
								gap: 1.5
							}}>
								<AudioIcon
									fontSize="small"
									color={isSelected ? "primary" : "action"}
								/>
								<ListItemText
									primary={audio.name}
									primaryTypographyProps={{
										fontWeight: isSelected ? 600 : 400,
										color: isSelected ? "primary" : "textPrimary"
									}}
								/>
							</Box>

							{isSelected && (
								<Zoom in={isSelected}>
									<Check
										fontSize="small"
										color="primary"
										sx={{ mr: 1 }}
									/>
								</Zoom>
							)}

							<Tooltip title={isPlaying ? "Parar" : "Reproduzir"}>
								<PlayButton
									size="small"
									isplaying={isPlaying ? "true" : "false"}
									onClick={(e) => {
										e.stopPropagation();
										onPlayAudio(audio.id);
									}}
									aria-label={`Reproduzir som ${audio.name}`}
								>
									{isPlaying ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
								</PlayButton>
							</Tooltip>
						</AudioListItem>
					);
				})}
			</List>
		</CategoryPaper>
	);
};

/**
 * ChangeNotificationsModal component
 */
const ChangeNotificationsModal = ({
	open,
	onClose,
	onConfirm,
	globalNotification,
	userNotification
}) => {
	const theme = useTheme();
	const [selections, setSelections] = useState({
		global: globalNotification || "sound",
		user: userNotification || "sound"
	});
	const [playingAudio, setPlayingAudio] = useState(null);
	const [audioInstance, setAudioInstance] = useState(null);

	// Reset audio playback when modal closes
	useEffect(() => {
		if (!open) {
			stopAudio();
		}
	}, [open]);

	// Update selections when props change
	useEffect(() => {
		if (open) {
			setSelections({
				global: globalNotification || "sound",
				user: userNotification || "sound"
			});
		}
	}, [open, globalNotification, userNotification]);

	// Handle audio playback
	const playAudio = useCallback((audioId) => {
		// Stop current audio if playing
		stopAudio();

		if (playingAudio === audioId) {
			setPlayingAudio(null);
			return;
		}

		// Find the audio file
		const audioFile = audioFiles.find(audio => audio.id === audioId);
		if (!audioFile) return;

		// Create and play new audio
		const audio = new Audio(audioFile.url);
		audio.addEventListener("ended", () => {
			setPlayingAudio(null);
		});

		audio.play().catch(error => {
			console.error("Error playing audio:", error);
			setPlayingAudio(null);
		});

		setAudioInstance(audio);
		setPlayingAudio(audioId);
	}, [playingAudio]);

	// Stop audio playback
	const stopAudio = useCallback(() => {
		if (audioInstance) {
			audioInstance.pause();
			audioInstance.currentTime = 0;
		}
		setPlayingAudio(null);
		setAudioInstance(null);
	}, [audioInstance]);

	// Handle audio selection change
	const handleAudioChange = useCallback((type, audioId) => {
		setSelections(prev => ({
			...prev,
			[type]: audioId
		}));
	}, []);

	// Handle confirmation
	const handleConfirm = () => {
		stopAudio();
		onConfirm({
			selectedAudioGlobal: selections.global,
			selectedAudioMy: selections.user
		});
	};

	// Handle close
	const handleClose = () => {
		stopAudio();
		onClose(false);
	};

	// Check if selections are valid
	const isValid = selections.global && selections.user;

	// Check if selections were changed
	const hasChanges = selections.global !== globalNotification ||
		selections.user !== userNotification;

	return (
		<StyledDialog
			open={open}
			onClose={handleClose}
			aria-labelledby="notification-sounds-dialog"
			BackdropProps={{
				style: {
					backdropFilter: "blur(5px)",
					backgroundColor: alpha(theme.palette.mode === "dark"
						? "#000"
						: "#6e7b8c", 0.5)
				}
			}}
			TransitionProps={{
				onExited: stopAudio
			}}
		>
			<DialogTitle sx={{ pb: 1 }}>
				<Box sx={{ textAlign: "center", py: 1 }}>
					<Typography variant="h5" fontWeight="600" color="primary">
						Sons de Notificações
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						Selecione os sons para diferentes tipos de notificações
					</Typography>
				</Box>
			</DialogTitle>

			<DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 2 }}>
				<Stack spacing={3}>
					<AudioSelectCategory
						title="Atribuídos a mim"
						icon={<NotificationsActive />}
						selectedAudio={selections.user}
						onAudioChange={(audioId) => handleAudioChange("user", audioId)}
						playingAudio={playingAudio}
						onPlayAudio={playAudio}
					/>

					<AudioSelectCategory
						title="Sem Atendimento"
						icon={<NotificationsOff />}
						selectedAudio={selections.global}
						onAudioChange={(audioId) => handleAudioChange("global", audioId)}
						playingAudio={playingAudio}
						onPlayAudio={playAudio}
					/>
				</Stack>
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
				<Button
					onClick={handleClose}
					variant="outlined"
					color="inherit"
				>
					Cancelar
				</Button>
				<Button
					onClick={handleConfirm}
					variant="contained"
					color="primary"
					disabled={!isValid || !hasChanges}
				>
					Salvar Alterações
				</Button>
			</DialogActions>
		</StyledDialog>
	);
};

export default ChangeNotificationsModal;